<template>
    <div
        id="lt-yantr"
        class="advanced-calc business-loan-calc">
        <div class="container-width">
            <div class="advanced-calc-filter">
                <div class="business-loan-information">
                    <label class="filter-header">Business information</label>
                    <ul class="filter-inputs three-col mob-ai-baseline">
                        <li class="business-start-date">
                            <label>{{ labelBusinessStartDate }}</label>
                            <drop-down
                                v-model="BusinessStartMonth"
                                :options="MonthList"
                                @input="calculateBusinessLoan()"
                            />
                            <drop-down
                                v-model="BusinessStartYear"
                                :options="YearList"
                                @input="calculateBusinessLoan()"
                            />
                        </li>
                        <li>
                            <number-input
                                v-model="AnnualRevenue"
                                :label="labelAnnualRevenue"
                                @input="calculateDeposit()"
                            />
                        </li>
                        <li>
                            <number-input
                                v-model="LastMonthDeposit"
                                :label="labelLastMonthsDeposits"
                                :tooltip-link="tooltipDeposit"
                                @input="calculateDeposit()"
                            />
                            <span
                                v-show="ShowDepositeError"
                                class="error-msg">Enter monthly deposit amount &lt; ${{ formatAmount(AnnualRevenue) }}</span>
                        </li>
                        <li>
                            <drop-down
                                v-model="CreditScore"
                                :label="labelCreditScore"
                                :options="CreditScoreList"
                                @input="calculateBusinessLoan()"
                            />
                            <a
                                href="/credit-score/"
                                class="font-sm">Get your credit score</a>
                        </li>
                        <li class="homebased">
                            <drop-down
                                v-model="HomeBased"
                                :label="labelHomebased"
                                :options="HomeBasedList"
                                :tooltip-link="tooltipHomeBased"
                                @input="calculateBusinessLoan()"
                            />
                            <a
                                href="javascript:void(0)"
                                class="reset-calc hidden-xs"
                                @click="resetCalculator()"><span class="lt-icons lt4-Refresh"></span> Reset Calculator</a>
                        </li>
                    </ul>
                </div>
            </div>
            <div class="business-loan-rate-reduction">
                <h2>You may be able to borrow between</h2>
                <h2 class="green-text">
                    ${{ formatAmount(OutputMin) }} - ${{ formatAmount(OutputMax) }}
                </h2>
                <div class="footer-section">
                    <button
                        class="btn btn-blue"
                        type="submit"
                        @click="redirectWithParams()">
                        {{ DestinationButtonText }}
                    </button>
                    <advertising-disclosures
                        v-if="DisclosureText"
                        :disclosure-text="DisclosureText"
                        :disclosure-tokens="DisclosureTokens"
                        class="disclosures"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Accounting from 'accounting';
    import NumberInput from '~/components/number-input.vue';
    import DropDown from '~/components/drop-down.vue';
    import AdvertisingDisclosures from '~/components/advertising-disclosures.vue';
    import BaseMixin from '~/mixin/base-mixin';
    import LoanTermList from '~/constants/loan-term-list';
    import CreditScoreList from '~/constants/credit-score-list';
    import MonthList from '~/constants/month-list';
    import DefaultTooltip from '~/constants/tooltip-defaults';

    export default {
        components: {
            NumberInput,
            DropDown,
            AdvertisingDisclosures,
        },
        mixins: [BaseMixin],
        props: {
            labelBusinessStartDate: {
                default: 'Business Start Date',
            },
            labelAnnualRevenue: {
                default: 'Annual Revenue',
            },
            labelLastMonthsDeposits: {
                default: 'Last Month\'s Deposits',
            },
            labelHomebased: {
                default: 'Home-Based',
            },
            labelCreditScore: {
                default: 'Est. Credit Score',
            },
            annualRevenue: {
                default: 300000,
            },
            tooltipDeposit: {
                default: DefaultTooltip.LastMonthsDeposits,
            },
            lastMonthDeposit: {
                default: 15000,
            },
            creditScore: {
                default: '1',
            },
            destinationButtonText: {
                default: 'View Free Offers',
            },
            businessStartMonth: {
                default: 1,
            },
            businessStartYear: {
                default: new Date().getFullYear() - 1,
            },
            homeBased: {
                default: 'No',
            },
            tooltipHomeBased: {
                default: DefaultTooltip.HomeBased,
            },
            disclosureTokens: {
                default: '01',
            },
            disclosureText: {
                default: 'Disclosures',
            },
            destinationLinkOrId: {
                default: 'wp-business',
            },
        },
        data() {
            return {
                LoanTermInYearsList: LoanTermList.LoanTermInYearsList.autorefinance,
                CreditScoreList: CreditScoreList.businessLoan,
                AnnualRevenue: this.annualRevenue,
                LastMonthDeposit: this.lastMonthDeposit,
                CreditScore: this.creditScore,
                DestinationButtonText: this.destinationButtonText,
                BusinessStartMonth: this.businessStartMonth,
                MonthList,
                BusinessStartYear: this.businessStartYear,
                YearList: [],
                HomeBasedList: [],
                HomeBased: this.homeBased,
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                DestinationLinkOrId: this.destinationLinkOrId,
                CurrentYear: this.businessStartYear + 1,
                CurrentMonth: new Date().getMonth() + 1,
                TimeInBusiness: 0,
                OutputMin: 0,
                OutputMax: 0,
                ShowDepositeError: false,
                UpdatedLastMonthDeposit: this.lastMonthDeposit,
            };
        },
        mounted() {
            this.calculateBusinessLoan();
        },
        created() {
            this.getHomeBasedList();
            this.getyearList();
        },
        methods: {
            calculateBusinessLoan() {
                this.TimeInBusiness = this.CurrentYear - this.BusinessStartYear + (this.CurrentMonth - this.BusinessStartMonth) / 12;
                const baseCalculation = (this.unformatFigure(this.AnnualRevenue) + this.UpdatedLastMonthDeposit * 12) / 2;
                let finalCalculation = 0;
                const NewbaseCalculation = 0.1 * baseCalculation;

                const CreditScore = Number(this.CreditScore);

                finalCalculation += CreditScore === 1 || CreditScore === 2 ? 0.0325 * baseCalculation : 0;
                finalCalculation -= CreditScore === 3 || CreditScore === 4 ? 0.04 * baseCalculation : 0;
                finalCalculation += this.TimeInBusiness >= 3 ? 0.0175 * baseCalculation : 0;
                finalCalculation -= this.HomeBased === 'Yes' ? 0.025 * baseCalculation : 0;
                this.OutputMin = (NewbaseCalculation + finalCalculation) * 0.8;
                this.OutputMax = (NewbaseCalculation + finalCalculation) * 1.2;
            },
            getyearList() {
                for (let year = this.CurrentYear; year >= this.CurrentYear - 100; year--) {
                    this.YearList.push({
                        text: year,
                        value: year,
                    });
                }
            },
            getHomeBasedList() {
                this.HomeBasedList.push({ text: 'Yes', value: 'Yes' }, { text: 'No', value: 'No' });
            },
            formatAmount(figure) {
                return Accounting.formatNumber(figure);
            },
            resetCalculator() {
                this.BusinessStartMonth = this.businessStartMonth;
                this.BusinessStartYear = this.businessStartYear;
                this.AnnualRevenue = this.formatFigure(this.annualRevenue);
                this.LastMonthDeposit = this.formatFigure(this.lastMonthDeposit);
                this.CreditScore = this.creditScore;
                this.HomeBased = this.homeBased;
                this.ShowDepositeError = false;
                this.UpdatedLastMonthDeposit = this.lastMonthDeposit;
                this.calculateBusinessLoan();
            },
            calculateDeposit() {
                if (this.unformatFigure(this.LastMonthDeposit) > this.unformatFigure(this.AnnualRevenue)) {
                    this.ShowDepositeError = true;
                    this.UpdatedLastMonthDeposit = this.unformatFigure(this.AnnualRevenue);
                } else {
                    this.ShowDepositeError = false;
                    this.UpdatedLastMonthDeposit = this.unformatFigure(this.LastMonthDeposit);
                }
                this.$nextTick(() => {
                    this.calculateBusinessLoan();
                });
            },
            redirectWithParams() {
                let creditScore;
                if (this.CreditScore === '1') {
                    creditScore = 'EXCELLENT';
                }
                if (this.CreditScore === '2') {
                    creditScore = 'SOMECREDITPROBLEMS';
                }
                if (this.CreditScore === '3') {
                    creditScore = 'MAJORCREDITPROBLEMS';
                }
                if (this.CreditScore === '4') {
                    creditScore = 'LITTLEORNOCREDITHISTORY';
                }
                const baseUrl = this.setDestinationBaseURL();
                const urlWithParams = `${baseUrl}&EstimatedCreditRating=${creditScore}&AnnualRevenue=${this.unformatFigure(this.AnnualRevenue)}&BusinessInceptionDate=${parseInt(this.BusinessStartMonth, 10).toLocaleString('en-us', { minimumIntegerDigits: 2 })}/01/${this.BusinessStartYear}`;
                window.location = urlWithParams;
            },
        },
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="../../assets/styles/calc-advanced-filter.scss" lang="scss"></style>
<!-- Below main.scss file is common for "Business Loan & Business Loan Refinance" Calculator. -->
<style src="./main.scss" lang="scss"></style>
